import { Component, ErrorHandler, OnInit, ChangeDetectionStrategy, signal } from '@angular/core'
import { form, max, min } from '@angular/forms/signals'
import { MatDialog } from '@angular/material/dialog'
import { MatSnackBar } from '@angular/material/snack-bar'
import { firstValueFrom } from 'rxjs'

import { FirebaseLemonator } from 'app/_angular/service/firebase-lemonator.service'
import {
  LLM_TOSITE_LUKU_TUOTANTO_KENTAT,
  LlmTositeLukuTuotantoKentanYhteenveto,
  LlmTositeLukuTuotantoKentta,
  LlmTositeLukuTuotantoRivi,
  LlmTositeLukuTuotantoYhteenvetoRivi,
} from '../../../_jaettu-lemonator/model/llm-tosite-luku-testaus'
import { LisaaTositteetTestisettiinDialog, LisaaTositteetTestisettiinDialogData, LisaaTositteetTestisettiinDialogResult } from './lisaa-tositteet-testisettiin.dialog'
import { callLlmTositeLukuTestaus } from './llm-tosite-luku-testaus.client'

interface Perustelu {
  reasoning: string | null
  raakaVastaus: string | null
}

const _isoPaiva = (paiva: Date): string => paiva.toISOString().slice(0, 10)

interface TuotantohakuLomake {
  alkaen: string
  asti: string
  suodatusKentta: LlmTositeLukuTuotantoKentta | 'kaikki'
  raja: number | null
}

@Component({
  selector: 'app-tosite-luku-tuotanto',
  templateUrl: './tosite-luku-tuotanto.component.html',
  styleUrls: ['./tosite-luku-tuotanto.component.css'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class TositeLukuTuotantoComponent implements OnInit {
  readonly kentat = LLM_TOSITE_LUKU_TUOTANTO_KENTAT
  readonly kenttaOtsikot: Record<LlmTositeLukuTuotantoKentta, string> = {
    paivamaara: 'Päivämäärä',
    summa: 'Summa',
    valuutta: 'Valuutta',
    toimittajaAvain: 'Toimittaja',
  }
  readonly kaikkiKentat = 'kaikki' as const

  lomake = form(
    signal<TuotantohakuLomake>({
      alkaen: _isoPaiva(new Date(new Date().getFullYear(), new Date().getMonth() - 2, 1)),
      asti: _isoPaiva(new Date()),
      suodatusKentta: this.kaikkiKentat,
      raja: 100,
    }),
    polku => {
      min(polku.raja, 1)
      max(polku.raja, 500)
    },
  )

  readonly yhteenveto = signal<LlmTositeLukuTuotantoYhteenvetoRivi[]>([])
  readonly osumattomat = signal<LlmTositeLukuTuotantoRivi[]>([])
  readonly lataa = signal(false)
  readonly perustelut = signal<ReadonlyMap<string, Perustelu>>(new Map())
  readonly avattuPoimintaAvain = signal<string | null>(null)
  readonly perusteluaLadataan = signal(false)

  constructor(
    private _firebase: FirebaseLemonator,
    private _snackbar: MatSnackBar,
    private _errorHandler: ErrorHandler,
    private _dialog: MatDialog,
  ) {}

  async ngOnInit() {
    await this.hae()
  }

  async hae() {
    if (this.lataa()) {
      return
    }
    this.lataa.set(true)
    this.avattuPoimintaAvain.set(null)
    try {
      const { alkaen, asti, suodatusKentta, raja } = this.lomake().value()
      const kentta = suodatusKentta === this.kaikkiKentat ? undefined : suodatusKentta
      const [yhteenveto, osumattomat] = await Promise.all([
        callLlmTositeLukuTestaus(this._firebase, 'haeTuotantotilasto', { alkaen, asti }),
        callLlmTositeLukuTestaus(this._firebase, 'haeTuotantoOsumattomat', { alkaen, asti, kentta, raja }),
      ])
      if (yhteenveto.e || osumattomat.e) {
        this._snackbar.open('Tuotantotilaston haku epäonnistui.', 'OK', { duration: 5000 })
        return
      }
      this.yhteenveto.set(yhteenveto.rivit ?? [])
      this.osumattomat.set(osumattomat.rivit ?? [])
    } catch (err) {
      this._snackbar.open('Tuotantotilaston haku epäonnistui.', 'OK', { duration: 5000 })
      this._errorHandler.handleError(err)
    } finally {
      this.lataa.set(false)
    }
  }

  prosentti(kentta: LlmTositeLukuTuotantoKentanYhteenveto): number | null {
    return kentta.arvioituja > 0 ? (kentta.osumia / kentta.arvioituja) * 100 : null
  }

  virheprosentti(rivi: LlmTositeLukuTuotantoYhteenvetoRivi): number {
    return rivi.poimintoja > 0 ? (rivi.virheita / rivi.poimintoja) * 100 : 0
  }

  tallennusprosentti(rivi: LlmTositeLukuTuotantoYhteenvetoRivi): number {
    return rivi.poimintoja > 0 ? (rivi.tallennettuja / rivi.poimintoja) * 100 : 0
  }

  async vaihdaPerustelu(rivi: LlmTositeLukuTuotantoRivi) {
    if (this.avattuPoimintaAvain() === rivi.poimintaAvain) {
      this.avattuPoimintaAvain.set(null)
      return
    }
    this.avattuPoimintaAvain.set(rivi.poimintaAvain)
    if (this.perustelut().has(rivi.poimintaAvain)) {
      return
    }
    this.perusteluaLadataan.set(true)
    try {
      const vastaus = await callLlmTositeLukuTestaus(this._firebase, 'haeTuotantoPerustelu', { poimintaAvain: rivi.poimintaAvain })
      if (vastaus.e) {
        this._snackbar.open('Perustelun haku epäonnistui.', 'OK', { duration: 5000 })
        return
      }
      this.perustelut.update(perustelut => new Map(perustelut).set(rivi.poimintaAvain, { reasoning: vastaus.reasoning ?? null, raakaVastaus: vastaus.raakaVastaus ?? null }))
    } catch (err) {
      this._snackbar.open('Perustelun haku epäonnistui.', 'OK', { duration: 5000 })
      this._errorHandler.handleError(err)
    } finally {
      this.perusteluaLadataan.set(false)
    }
  }

  voiLisataTestisettiin(rivi: LlmTositeLukuTuotantoRivi): boolean {
    return !!rivi.asiakasAvain && !!rivi.asiakasId && !!rivi.tositeAvain
  }

  async lisaaTestisettiin(rivi: LlmTositeLukuTuotantoRivi) {
    if (!this.voiLisataTestisettiin(rivi)) {
      return
    }
    const data: LisaaTositteetTestisettiinDialogData = {
      kohteet: [
        {
          tyyppi: 'lemonaid',
          asiakasAvain: rivi.asiakasAvain,
          asiakasId: rivi.asiakasId,
          asiakasNimi: rivi.asiakasNimi ?? rivi.asiakasAvain,
          tositeAvain: rivi.tositeAvain,
        },
      ],
    }
    const tulos = await firstValueFrom(
      this._dialog
        .open<LisaaTositteetTestisettiinDialog, LisaaTositteetTestisettiinDialogData, LisaaTositteetTestisettiinDialogResult>(LisaaTositteetTestisettiinDialog, {
          data,
          maxWidth: '95vw',
          disableClose: true,
        })
        .afterClosed(),
    )
    if (tulos?.lisatty) {
      this._snackbar.open('Tosite lisättiin testisettiin.', 'OK', { duration: 5000 })
    }
  }
}
